import { Failure } from '@mrhenry/failure';
import { init } from '@mrhenry/wp--mr-interactive';
import { render } from 'lit';
import { createStore } from 'redux';
import { getWPHousePostsWithAllowNewReservations } from './api/allow-new-reservations';
import { generatePropertyDataUpdateLatch, PropertyDataUpdateLatch } from './api/get-calendar-data-for-house';
import { steps } from './components/steps/index';
import { stepError } from './components/steps/step-error';
import { bookingReducer } from './reducers';
import { staticText } from './static-text';
import { scrollStepIntoView, Step } from './types/steps';
import { WPContent, WPHousePosts, WPContentSource, WPHousePostsSource, wpContentDataFormatter, wpPostsDataFormatter } from './types/wp-data';

declare global {
	interface Window {
		Mr: {
			theaddressesApiDomain: string;
		};
	}
}

async function startBookingApp( bookingEl: HTMLElement ) {
	const data = getDataFromBookingEl( bookingEl );
	Failure.panicOnFailure( data );

	const posts = await getWPHousePostsWithAllowNewReservations( data.posts );
	if ( Failure.isFailure( posts ) ) {
		render( stepError( staticText.somethingWentWrongServerIssue, posts.message ), bookingEl );

		return;
	}

	const bookingStore = createStore( bookingReducer );
	const dispatch = bookingStore.dispatch;
	let currentStep: Step = bookingStore.getState().currentStep;

	const propertyDataUpdateLatch: PropertyDataUpdateLatch = generatePropertyDataUpdateLatch( dispatch );

	bookingStore.subscribe( () => {
		render(
			steps(
				bookingStore.getState(),
				dispatch,
				propertyDataUpdateLatch,
				data.assetUrl,
				data.content,
				posts
			), bookingEl
		);
		if ( currentStep !== bookingStore.getState().currentStep ) {
			scrollStepIntoView( bookingStore.getState().currentStep );
			currentStep = bookingStore.getState().currentStep;
		}
	} );

	render(
		steps(
			bookingStore.getState(),
			dispatch,
			propertyDataUpdateLatch,
			data.assetUrl,
			data.content,
			posts
		),
		bookingEl
	);
}

function getDataFromBookingEl( bookingEl: HTMLElement ): Failure|{ posts: WPHousePosts, content: WPContent, assetUrl: string } {
	let content: WPContentSource | null;
	let posts: WPHousePostsSource | null;

	// Ensure that 'data-asset-url' is available and valid.
	if ( !( 'assetUrl' in bookingEl.dataset ) ) {
		return Failure.from( '"data-asset-url" must be provided' );
	}

	const assetUrl = bookingEl.dataset.assetUrl;

	// Ensure that 'data-content' is available and valid.
	if ( !( 'content' in bookingEl.dataset ) ) {
		return Failure.from( '"data-content" must be provided' );
	}

	try {
		content = JSON.parse( bookingEl.dataset.content || '' );
	} catch ( err ) {
		return Failure.from( err ).context( '"data-content" must be a valid json string' );
	}

	// Ensure that 'data-posts' is available and valid.
	if ( !( 'posts' in bookingEl.dataset ) ) {
		return Failure.from( '"data-posts" must be provided' );
	}

	try {
		posts = JSON.parse( bookingEl.dataset.posts || '' );
	} catch ( err ) {
		return Failure.from( err ).context( '"data-posts" must be a valid json string' );
	}

	if ( !assetUrl || !content || !posts ) {
		return Failure.from( '"data-asset-url", "data-content" and "data-posts" must be provided and must not be falsy' );
	}

	return {
		assetUrl: assetUrl,
		content: wpContentDataFormatter( content ),
		posts: wpPostsDataFormatter( posts ),
	};
}

init( (): boolean => {
	const bookingEl = document.getElementById( 'booking' );
	if ( !bookingEl ) {
		return false;
	}


	// Remove all children.
	while ( bookingEl.firstChild ) {

		bookingEl.removeChild( bookingEl.lastChild! );
	}

	startBookingApp( bookingEl );

	return true;
} );
